/*
 * @Description:
 * @Author: Jack Deng
 * @Date: 2021-11-10 11:31:51
 * @LastEditors: Jack Deng
 * @LastEditTime: 2022-07-19 17:12:36
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesChannelContentEditor/SalesChannelEditorNavigation/slice/index.ts
 */
import { isCurrentPrimary } from '@/src/constrains/SalesChannelMultiLanguageSelect';
import {
  assData,
  defaultStatus,
  generateDefaultState,
} from '@/src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  getSalesChannelContentNavigation,
  postSalesChannelContentNavigation,
} from '@/src/request/salesChannel/content';
import { randomNumber } from '@/src/tools/helper';
import { getI18nFromGeneral } from '@/src/tools/tools';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import arrayMove from 'array-move';
import {
  onCollapseOpen as _onCollapseOpen,
  hasError,
} from 'src/pages/SalesChannelContentEditor/slice/reduxCommonFunctions';
import {
  EnumReducers,
  getCollapseOpenStatus as _getCollapseOpenStatus,
  getCollapseValidationMsgDisplay as _getCollapseValidationMsgDisplay,
  getHasModified as _getHasModified,
  getLoaded as _getLoaded,
  getSalesChannel as _getSalesChannel,
  getSalesChannelName as _getSalesChannelName,
  getState as _getState,
  getSubmitStamp as _getSubmitStamp,
} from 'src/pages/SalesChannelContentEditor/slice/selectors';
import { msg } from 'src/tools/message';
import { convertDataFromServerToRedux } from '../tool';
import { generateNoneLinkData } from 'src/constrains/LinkLibrary/tools';

export const fetchNavigation = createAsyncThunk(
  'fetchSalesChannelContentNavigation',
  async (data: any) => {
    const { uuid, params } = data;
    const isPrimary = isCurrentPrimary();
    const response = await getSalesChannelContentNavigation(uuid, params);
    return {
      response,
      isPrimary,
    };
  },
);

export const submitNavigation = createAsyncThunk(
  'submitSalesChannelContentNavigation',
  async ({ body, params }: any) => {
    const uuid = body?.salesChannelContent?.uuid;
    const contentNavigations = body?.contentNavigations || [];
    const response = await postSalesChannelContentNavigation(
      uuid,
      contentNavigations,
      params,
    );
    return {
      response,
    };
  },
);

export const getNavigations = () => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.navigation].contentNavigations,
    (contentNavigations) => {
      return contentNavigations;
    },
  );
};

export const getNavigation = (id: any) => {
  return createSelector(
    (state: any) =>
      state.salesChannelContent[EnumReducers.navigation].contentNavigations,
    (contentNavigations) => {
      const navigation = contentNavigations.find((v) => v.uuid === id);
      return navigation;
    },
  );
};

export const getState = () => {
  return _getState(EnumReducers.navigation);
};

export const getCollapseValidationMsgDisplay = (id) => {
  return _getCollapseValidationMsgDisplay(EnumReducers.navigation, id);
};

export const getCollapseOpenStatus = (id) => {
  return _getCollapseOpenStatus(EnumReducers.navigation, id);
};

export const getSubmitStamp = () => {
  return _getSubmitStamp(EnumReducers.navigation);
};

export const getHasModified = () => {
  return _getHasModified(EnumReducers.navigation);
};

export const getSalesChannel = () => {
  return _getSalesChannel(EnumReducers.navigation);
};

export const getSalesChannelName = () => {
  return _getSalesChannelName(EnumReducers.navigation);
};

export const getLoaded = () => {
  return _getLoaded(EnumReducers.navigation);
};

const _defaultState = generateDefaultState([]);

const defaultState = {
  ..._defaultState,
  ...defaultStatus,
  contentNavigations: [],
};

const salesChannelContentNavigation = createSlice({
  name: 'salesChannelContentNavigation',
  initialState: defaultState,
  reducers: {
    resetData(state) {
      assData(state, defaultState);
    },
    onCollapseOpen(state, action) {
      const { idArr = [] } = action.payload; //=>[id:string]
      _onCollapseOpen(state, idArr);
    },
    onErrorChange(state, action) {
      const { id, status } = action.payload;
      state.collapseErrorMsgDisplay[id].hasError = status;
    },
    onTitleChange(state, action) {
      const { uuid, value } = action.payload;
      const index = state?.contentNavigations?.findIndex(
        (v) => v.uuid === uuid,
      );
      if (index !== -1) {
        state.contentNavigations[index].title = value;
        state.hasModified = true;
      }
    },
    onTitleLinkChange(state, action) {
      const { uuid, value } = action.payload;
      const index = state?.contentNavigations?.findIndex(
        (v) => v.uuid === uuid,
      );
      if (index !== -1) {
        state.contentNavigations[index].linkModule = value;
        state.hasModified = true;
      }
    },
    onMenuTitleChange(state, action) {
      const { navigationUuid, index, value } = action.payload;
      const navigationIndex = state?.contentNavigations?.findIndex(
        (v) => v.uuid === navigationUuid,
      );
      if (index !== -1) {
        state.contentNavigations[navigationIndex].menuItems[index].title =
          value;
        state.hasModified = true;
      }
    },
    onMenuLinkChange(state, action) {
      const { navigationUuid, index, value } = action.payload;
      const navigationIndex = state?.contentNavigations?.findIndex(
        (v) => v.uuid === navigationUuid,
      );
      if (index !== -1) {
        state.contentNavigations[navigationIndex].menuItems[index].linkModule =
          value;
        state.hasModified = true;
      }
    },
    onAddMenu(state, action) {
      const { uuid } = action.payload;
      const navigationIndex = state?.contentNavigations?.findIndex(
        (v) => v.uuid === uuid,
      );
      if (navigationIndex !== -1) {
        const items = state.contentNavigations[navigationIndex].menuItems;

        const num = (items || []).length;

        items.push({
          title: `Menu ${num + 1}`,
          isNew: true,
          linkModule: generateNoneLinkData(),
          uuid: randomNumber(),
        });
        state.contentNavigations[navigationIndex].menuItems = items;
        state.hasModified = true;
      }
    },
    onDeleteMenu(state, action) {
      const { uuid, index } = action.payload;
      const navigationIndex = state?.contentNavigations?.findIndex(
        (v) => v.uuid === uuid,
      );
      if (navigationIndex !== -1) {
        state.contentNavigations[navigationIndex].menuItems.splice(index, 1);
        state.hasModified = true;
      }
    },
    onSortMenu(state, action) {
      const { uuid, newIndex, oldIndex } = action.payload;
      const navigationIndex = state?.contentNavigations?.findIndex(
        (v) => v.uuid === uuid,
      );
      if (navigationIndex !== -1) {
        state.contentNavigations[navigationIndex].menuItems = arrayMove(
          state.contentNavigations[navigationIndex].menuItems,
          oldIndex,
          newIndex,
        );
        state.hasModified = true;
      }
    },
    onAddNavigation(state, action) {
      const id = randomNumber();
      const num = (state.contentNavigations || []).length;
      state.contentNavigations.push({
        uuid: id,
        menuItems: [],
        title: 'Group' + `${num + 1}`,
        isNew: true,
        linkModule: generateNoneLinkData(),
      });
      state.collapseErrorMsgDisplay[id] = { hasError: false, display: false };
      state.collapseOpen[id] = false;
      state.hasModified = true;
    },
    onCloneNavigation(state, action) {
      const { uuid } = action.payload;
      const navigation = state?.contentNavigations?.find(
        (v) => v.uuid === uuid,
      );
      if (navigation) {
        const id = randomNumber();
        state.contentNavigations.push({ ...navigation, uuid: id, isNew: true });
        state.collapseErrorMsgDisplay[id] = { hasError: false, display: false };
        state.collapseOpen[id] = false;
        state.hasModified = true;
      }
    },
    onDeleteNavigation(state, action) {
      const { uuid } = action.payload;
      const navigationIndex = state?.contentNavigations?.findIndex(
        (v) => v.uuid === uuid,
      );
      if (navigationIndex !== -1) {
        state.contentNavigations.splice(navigationIndex, 1);
        delete state.collapseErrorMsgDisplay[uuid];
        state.hasModified = true;
      }
    },
    validation(state) {
      hasError(state, state.collapseErrorMsgDisplay || {});
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchNavigation.fulfilled, (state, action) => {
      const { response } = action.payload;
      const _data = convertDataFromServerToRedux(response) || {};
      assData(state, _data);
    });

    builder.addCase(submitNavigation.fulfilled, (state, action) => {
      // const { response } = action.payload;
      msg.success(getI18nFromGeneral('general.status.changessavesuccessfully'));
      state.hasModified = false;
      // assData(state, { ...response });
    });
  },
});

export const {
  resetData,
  onCollapseOpen,
  onErrorChange,
  validation,
  onTitleChange,
  onTitleLinkChange,
  onMenuTitleChange,
  onMenuLinkChange,
  onAddMenu,
  onDeleteMenu,
  onSortMenu,
  onDeleteNavigation,
  onCloneNavigation,
  onAddNavigation,
} = salesChannelContentNavigation.actions;

export default salesChannelContentNavigation.reducer;
